import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Heart, Pencil, Play, X } from "lucide-react";
import { EditTagsDialog } from "@/components/music/EditTagsDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { startDrag } from "@/lib/music/drag";

const COLS = [["title", "Title"], ["artist", "Artist"], ["album", "Album"], ["genre", "Genre"], ["year", "Year"]];
const PAGE = 500;
const val = (t, k) => (k === "track" ? (t.track ? (t.disk || 1) * 1000 + t.track : "") : t[k]);
const trackNo = (t) => (t.track ? `${t.disk > 1 ? `${t.disk}-` : ""}${t.track}` : "");

export const SongTable = ({ m, list, label, sel, setSel, onRemove, showTrack, initialSort = { k: "artist", dir: 1 } }) => {
  const [sort, setSort] = useState(initialSort);
  const [editing, setEditing] = useState(null);
  const cols = showTrack ? [["track", "#"], ...COLS] : COLS;
  const [shown, setShown] = useState(PAGE);
  const rows = useMemo(() => [...list].sort((a, b) => {
    const x = String(val(a, sort.k) ?? ""), y = String(val(b, sort.k) ?? "");
    if (!x || !y) return x ? -1 : y ? 1 : 0;
    return sort.dir * x.localeCompare(y, undefined, { numeric: true, sensitivity: "base" });
  }), [list, sort]);
  const paths = rows.map((t) => t.path);
  const toggle = (p) => setSel((s) => { const n = new Set(s); if (n.has(p)) n.delete(p); else n.add(p); return n; });
  const allOn = rows.length > 0 && rows.every((t) => sel.has(t.path));
  const dragPaths = (t) => (sel.has(t.path) ? [...sel] : [t.path]);
  return (
    <div data-testid="insp-song-table">
      <table className="w-full table-fixed text-sm">
        <thead className="sticky top-0 z-10 bg-[#0B0D10]">
          <tr className="border-b border-[#2D3442] text-left text-xs text-[#A8A090]">
            <th className="w-8 py-2"><Checkbox checked={allOn} onCheckedChange={(v) => setSel(v ? new Set(paths) : new Set())} data-testid="insp-select-all" /></th>
            <th className="w-8" />
            {cols.map(([k, l]) => (
              <th key={k} className={`py-2 font-normal ${k === "year" || k === "track" ? "w-14" : k === "genre" ? "w-32" : ""}`}>
                <button onClick={() => setSort((s) => ({ k, dir: s.k === k ? -s.dir : 1 }))} className={`inline-flex items-center gap-1 hover:text-[#F3EBDD] ${sort.k === k ? "text-[#FBBF24]" : ""}`} data-testid={`insp-sort-${k}`}>
                  {l}{sort.k === k && (sort.dir > 0 ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
                </button>
              </th>
            ))}
            <th className="w-40 py-2 font-normal">Moods</th>
            <th className="w-16" />
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, shown).map((t, i) => (
            <tr key={t.path} draggable onDragStart={(e) => startDrag(e, dragPaths(t), t.title)} onDoubleClick={() => m.play(paths, t.path, label)}
              className={`group cursor-grab border-b border-[#1A202C] transition-colors hover:bg-[#13171F] ${m.current?.path === t.path ? "bg-[#D97706]/10" : sel.has(t.path) ? "bg-[#1A202C]" : ""}`} data-testid={`insp-row-${i}`}>
              <td className="py-1.5"><Checkbox checked={sel.has(t.path)} onCheckedChange={() => toggle(t.path)} data-testid={`insp-row-select-${i}`} /></td>
              <td><button onClick={() => m.play(paths, t.path, label)} className="text-[#7A7468] hover:text-[#F59E0B]" title="Play" data-testid={`insp-row-play-${i}`}><Play className="h-3.5 w-3.5" /></button></td>
              {showTrack && <td className="tabular-nums text-[#7A7468]" data-testid={`insp-row-track-${i}`}>{trackNo(t)}</td>}
              <td className={`truncate pr-3 ${m.current?.path === t.path ? "text-[#FBBF24]" : "text-[#F3EBDD]"}`} title={t.path}>{m.favs.has(t.path) && <Heart className="mr-1 inline h-3 w-3 fill-[#F87171] text-[#F87171]" />}{t.title}</td>
              <td className="truncate pr-3 text-[#D6CCBC]">{t.artist}</td>
              <td className="truncate pr-3 text-[#A8A090]">{t.album}</td>
              <td className="truncate pr-3 text-[#A8A090]">{t.genre}</td>
              <td className="tabular-nums text-[#A8A090]">{t.year}</td>
              <td className="truncate text-[11px] text-[#C9BFAE]">{(m.moods[t.path] || []).join(", ")}</td>
              <td className="whitespace-nowrap"><button onClick={() => setEditing(t.path)} title="Edit details" className="mr-2 text-[#7A7468] opacity-0 hover:text-[#F3EBDD] group-hover:opacity-100" data-testid={`insp-row-edit-${i}`}><Pencil className="h-3.5 w-3.5" /></button>{onRemove && <button onClick={() => onRemove(t.path)} title="Remove" className="text-[#7A7468] opacity-0 hover:text-[#F87171] group-hover:opacity-100" data-testid={`insp-row-remove-${i}`}><X className="h-3.5 w-3.5" /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > shown && <button onClick={() => setShown((s) => s + PAGE)} className="mt-3 w-full text-center text-xs text-[#F59E0B] hover:underline" data-testid="insp-show-more">Show more ({(rows.length - shown).toLocaleString()} left)</button>}
      {!rows.length && <p className="py-12 text-center text-sm text-[#7A7468]" data-testid="insp-empty">No songs here yet. Drag some in from All music.</p>}
      {editing && <EditTagsDialog m={m} paths={[editing]} mode="song" open={!!editing} onOpenChange={(v) => !v && setEditing(null)} />}
    </div>
  );
};
